import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import "./ProductPage.css";
import {
  getFirestore,
  doc,
  getDoc,
  collection,
  getDocs,
} from "firebase/firestore";
import { app } from "../firebase";
import Loading from "../components/Loading";
import Navbar from "../components/Navbar";

const ProductPage = () => {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [price, setPrice] = useState(null);
  const [crossSell, setCrossSell] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);

  const db = getFirestore(app);

  // Scroll to top on product change
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [id]);

  // Fetch product and related cross-sells
  useEffect(() => {
    const fetchProduct = async () => {
      try {
        setLoading(true);

        // Fetch main product
        const productDoc = await getDoc(doc(db, "products", id));
        if (!productDoc.exists()) {
          setProduct(null);
          setLoading(false);
          return;
        }

        const productData = productDoc.data();
        setProduct({
          id: productDoc.id,
          name: productData.name,
          images: productData.images || [],
          description: productData.description || "",
          details: productData.details || "",
          stripePaymentLink: productData.stripePaymentLink || null,
        });

        // Set main product image
        setSelectedImage(productData.images?.[0] || "/images/default.png");

        // Fetch product price
        const pricesSnapshot = await getDocs(collection(db, "products", id, "prices"));
        let activePrice = null;
        pricesSnapshot.forEach((priceDoc) => {
          const data = priceDoc.data();
          if (data.active && !activePrice) {
            activePrice = { id: priceDoc.id, ...data };
          }
        });
        setPrice(activePrice);

        // Fetch cross-sell products
        const productsSnapshot = await getDocs(collection(db, "products"));
        const otherProducts = [];

        for (const docSnap of productsSnapshot.docs) {
          if (docSnap.id === id) continue; // skip current product
          const data = docSnap.data();

          // Fetch subcollection prices for each product
          const subPrices = await getDocs(collection(db, "products", docSnap.id, "prices"));
          let activeSubPrice = null;
          subPrices.forEach((subDoc) => {
            const subData = subDoc.data();
            if (subData.active && !activeSubPrice) {
              activeSubPrice = (subData.unit_amount / 100).toFixed(2);
            }
          });

          otherProducts.push({
            id: docSnap.id,
            name: data.name,
            images: data.images,
            price: activeSubPrice,
          });
        }

        // Shuffle and pick 3
        const shuffled = otherProducts.sort(() => 0.5 - Math.random()).slice(0, 3);
        setCrossSell(shuffled);

      } catch (error) {
        console.error("Error fetching product:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
  }, [id, db]);

  const handleBuy = () => {
    if (!product?.stripePaymentLink) {
      alert("Buy link not available.");
      return;
    }
    window.open(product.stripePaymentLink, "_blank");
  };

  if (loading) return <Loading />;
  if (!product) return <p>Product not found.</p>;

  return (
    <div className="ProductPage_Component">
      <Navbar />

      <div className="ProductPage_Container">
        {/* === Product Gallery === */}
        <div className="ProductPage_Gallery">
          <div className="ProductPage_MainImage">
            <img
              src={selectedImage}
              alt={product.name}
              className="MainImage"
            />
          </div>

          {product.images?.length > 1 && (
            <div className="ProductPage_Thumbnails">
              {product.images.map((img, index) => (
                <img
                  key={index}
                  src={img}
                  alt={`${product.name} ${index + 1}`}
                  className={`Thumbnail ${img === selectedImage ? "active" : ""}`}
                  onClick={() => setSelectedImage(img)}
                />
              ))}
            </div>
          )}
        </div>

        {/* === Product Info === */}
        <div className="ProductPage_Info">
          <h1>{product.name}</h1>
          {price && (
            <p className="ProductPage_Price">
              ${(price.unit_amount / 100).toFixed(2)}
            </p>
          )}
          <p className="ProductPage_Description">{product.description}</p>

          {product.stripePaymentLink ? (
            <button className="Custom_Buy_Button" onClick={handleBuy}>
              Buy Now
            </button>
          ) : (
            <p>Buy link not available.</p>
          )}
<div className="Product_Details">
  {product.details.split("\n").map((line, index) => {
    // Split each line into "label" and "value"
    const [label, ...rest] = line.split(":");
    const value = rest.join(":").trim(); // In case there's another ":" later
    return (
      <p key={index}>
        {value ? (
          <div className="Product_Details_Test">
            <strong>{label.trim()}:</strong> {value}
          </div>
        ) : (
          line // if the line doesn't have a ":", just show it normally
        )}
      </p>
    );
  })}
</div>

        </div>
      </div>
      {crossSell.length > 0 && (
        <div className="ProductPage_CrossSell">
          <h2>You may also like</h2>
          <div className="CrossSell_Grid">
            {crossSell.map((item) => (
              <Link key={item.id} to={`/product/${item.id}`} className="CrossSell_Card">
                <img
                  src={item.images?.[0] || "/images/default.png"}
                  alt={item.name}
                />
                <p>{item.name}</p>
                {item.price && <p className="CrossSell_Price">${item.price}</p>}
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductPage;
